import { Rng } from '../core/rng';
import { angleDelta, clamp } from '../core/math';
import type { Difficulty } from './difficulty';

export interface WindStep {
  /** Seconds from the start of the incident. */
  t: number;
  dir: number;
  speed: number;
}

export interface Wind {
  /** Direction of travel in radians, screen space. -PI/2 blows up the mountain. */
  dir: number;
  /** 0..1-ish, includes the current gust. */
  speed: number;
  baseSpeed: number;
  targetDir: number;
  targetSpeed: number;
  gust: number;
  /** Unit vector of travel. */
  ux: number;
  uy: number;
  /** Published ahead of time so the player can plan. This is what keeps the wind fair. */
  forecast: WindStep[];
  next: number;
  rng: Rng;
  gustiness: number;
}

const UPSLOPE = -Math.PI / 2;

export function makeWind(rng: Rng, diff: Difficulty): Wind {
  const dir0 = UPSLOPE + rng.range(-0.35, 0.35);
  const forecast: WindStep[] = [];
  let dir = dir0;
  for (let i = 1; i <= 40; i++) {
    dir += rng.range(-1, 1) * diff.shiftMag;
    // Keep the wind roughly up the slope: a wind blowing down the slope would
    // make the game too easy.
    dir = UPSLOPE + clamp(angleDelta(UPSLOPE, dir), -1.5, 1.5);
    forecast.push({
      t: i * diff.shiftInterval,
      dir,
      speed: clamp(diff.windBase + rng.range(-1, 1) * diff.windVar, 0.06, 1),
    });
  }
  return {
    dir: dir0,
    speed: diff.windBase,
    baseSpeed: diff.windBase,
    targetDir: dir0,
    targetSpeed: diff.windBase,
    gust: 0,
    ux: Math.cos(dir0),
    uy: Math.sin(dir0),
    forecast,
    next: 0,
    rng,
    gustiness: diff.gustiness,
  };
}

export function stepWind(w: Wind, elapsed: number, dt: number): void {
  while (w.next < w.forecast.length && elapsed >= w.forecast[w.next].t) {
    w.targetDir = w.forecast[w.next].dir;
    w.targetSpeed = w.forecast[w.next].speed;
    w.next++;
  }
  // A change takes about fifteen seconds to arrive, which is enough warning to move.
  w.dir += angleDelta(w.dir, w.targetDir) * Math.min(1, dt / 15);
  w.baseSpeed += (w.targetSpeed - w.baseSpeed) * Math.min(1, dt / 15);
  w.gust += (w.rng.next() - 0.5) * w.gustiness * dt * 6 - w.gust * dt * 0.9;
  w.gust = clamp(w.gust, -0.25, 0.45);
  w.speed = clamp(w.baseSpeed + w.gust, 0.02, 1.3);
  w.ux = Math.cos(w.dir);
  w.uy = Math.sin(w.dir);
}

/** Seconds until the next published wind change, or null when the forecast runs out. */
export function nextShiftIn(w: Wind, elapsed: number): number | null {
  const s = w.forecast[w.next];
  return s ? s.t - elapsed : null;
}

export const nextShift = (w: Wind): WindStep | undefined => w.forecast[w.next];
